import { getPage, Page, Routes } from 'constants/pages';
import { User } from 'types/User';
import { checkForPermission, Permission } from 'utils/permissions';
import { checkSegmentation } from 'utils/permissions/segmentation';

interface Overrides {
  title: string;
}
export interface SingularMenuItem {
  type: 'flat';
  page: Page;
  overrides?: Partial<Overrides>;
}

export interface DropdownMenuItem {
  type: 'nested';
  title: string;
  /** TODO: We should be able to support nested menus but this is not required atm  */
  children: SingularMenuItem[];
}

export type MenuItemType = SingularMenuItem | DropdownMenuItem;

export function featureFlagItem<T = SingularMenuItem | DropdownMenuItem>(
  enable: boolean,
  item: T,
  alternate?: T
): T[] {
  const backup = alternate ? [alternate] : [];
  return enable ? [item] : backup;
}

export const menuItemFactory: (
  route: Routes,
  overrides?: Partial<Overrides>
) => SingularMenuItem = (route, overrides) => {
  return { type: 'flat', page: getPage(route) as Page, overrides };
};

/**
 * Given a list of menu items and a list of permissions, return
 * only the items that the user has permissions for
 * @param items
 * @param permissions
 */
export const permissionCheckItems: (
  items: MenuItemType[],
  permissions: Permission[],
  user: User | null
) => MenuItemType[] = (items, permissions, user) => {
  let newItems = items
    .map((item) => {
      if (item.type === 'flat') {
        const requirement = item.page.requiredPermissions;
        const hasCompanyAdmin =
          !item.page.isCompanyAdmin || (item.page.isCompanyAdmin && user?.is_admin);
        const hasSuperUser =
          !item.page.isSuperUser || (item.page.isSuperUser && user?.is_superuser);
        const hasPermission = checkForPermission(requirement, permissions);

        const belongsToSegment = checkSegmentation(
          user?.company?.segmentation,
          item.page.segmentation
        );

        return hasCompanyAdmin && hasSuperUser && (hasPermission || belongsToSegment)
          ? item
          : undefined;
      }
      return {
        ...item,
        children: permissionCheckItems(item.children, permissions, user).filter(
          (i) => i !== undefined
        ),
      };
    })
    .filter(
      (i) => i !== undefined && (i.type !== 'nested' || i.children.length > 0)
    ) as MenuItemType[];

  // Given a list of items, if an item has only one child, replace that item with its child
  newItems = newItems.map((item) => {
    if (item.type === 'nested' && item.children.length === 1) {
      return item.children[0];
    }
    return item;
  });

  return newItems;
};

export const getNavigationItems: (
  permissions: Permission[],
  user: User | null
) => MenuItemType[] = (permissions, user) => {
  const items = [
    menuItemFactory(Routes.ShipmentsDashboard),
    {
      type: 'nested',
      title: 'Container Tracking',
      children: [
        menuItemFactory(Routes.ContainerTracking),
        menuItemFactory(Routes.ShipmentsActive, { title: 'Watchlist' }),
      ],
    } as DropdownMenuItem,
    menuItemFactory(Routes.RegionalEmptyAppointments),
    {
      type: 'nested',
      title: 'Fight Per Diem',
      children: [
        menuItemFactory(Routes.RegionalPerDiemArchive),
        menuItemFactory(Routes.RegionalTerminalScreenshots),
      ],
    } as DropdownMenuItem,
    {
      type: 'nested',
      title: 'Schedules',
      children: [
        menuItemFactory(Routes.RegionalGateSchedule),
        menuItemFactory(Routes.RegionalEmptyReturnLocations),
        menuItemFactory(Routes.VesselSchedule),
      ],
    } as DropdownMenuItem,
  ];

  return permissionCheckItems(items, permissions, user);
};

export const getUserProfileItems: (
  permissions: Permission[],
  user: User | null
) => DropdownMenuItem[] = (permissions, user) => {
  const items: DropdownMenuItem[] = [
    {
      type: 'nested',
      title: 'Settings',
      children: [
        menuItemFactory(Routes.Profile),
        menuItemFactory(Routes.Plans),
        menuItemFactory(Routes.InvoiceHistory),
        menuItemFactory(Routes.RegionalNotificationSettings),
        menuItemFactory(Routes.RegionalCredentials),
        menuItemFactory(Routes.AdminGateSchedule),
        menuItemFactory(Routes.AdminPtscSchedule),
      ],
    },
  ];

  return permissionCheckItems(items, permissions, user) as DropdownMenuItem[];
};
